import { StateCreator } from 'zustand';
import { Node, Edge } from '@xyflow/react';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('historySlice');
import { PipelineSlice } from './pipelineSlice';

export interface HistoryEntry {
  nodes: Node[];
  edges: Edge[];
  description: string;
  timestamp: number;
}

export interface HistoryState {
  past: HistoryEntry[];
  future: HistoryEntry[];
}

export interface HistorySlice {
  // State - history per pipeline (using plain object for localStorage compatibility)
  pipelineHistory: Record<string, HistoryState>;
  
  // Undo/Redo
  saveToHistory: (description: string) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;
  clearPipelineHistory: (pipelineId: string) => void; // New: clear history for specific pipeline
}

const MAX_HISTORY_SIZE = 50;

export const createHistorySlice: StateCreator<
  PipelineSlice & HistorySlice,
  [],
  [],
  HistorySlice
> = (set, get) => ({
  // Initial state
  pipelineHistory: {},

  saveToHistory: (description: string) => {
    // Note: This should be called BEFORE making a change to save the current state
    // so that undo can restore to it
    const state = get();
    const pipelineId = state.activePipelineId;
    
    if (!pipelineId) {
      logger.debug('Cannot save to history: no active pipeline');
      return;
    }
    
    const pipeline = state.pipelines.find(p => p.id === pipelineId);
    if (!pipeline) {
      logger.debug('Cannot save to history: pipeline not found');
      return;
    }
    
    const history = state.pipelineHistory[pipelineId] || { past: [], future: [] };
    
    // Check if this would be a duplicate of the last entry (skip if so)
    const lastEntry = history.past[history.past.length - 1];
    if (lastEntry) {
      const currentNodesStr = JSON.stringify(pipeline.nodes);
      const currentEdgesStr = JSON.stringify(pipeline.edges);
      const lastNodesStr = JSON.stringify(lastEntry.nodes);
      const lastEdgesStr = JSON.stringify(lastEntry.edges);
      
      if (currentNodesStr === lastNodesStr && currentEdgesStr === lastEdgesStr) {
        logger.debug('Skipping duplicate history entry');
        return;
      }
    }
    
    // Create snapshot of CURRENT state (before the change)
    const snapshot: HistoryEntry = {
      nodes: JSON.parse(JSON.stringify(pipeline.nodes)),
      edges: JSON.parse(JSON.stringify(pipeline.edges)),
      description,
      timestamp: Date.now(),
    };
    
    // Add to past, clear future (branching history)
    const newPast = [...history.past, snapshot];
    
    // Limit history size
    if (newPast.length > MAX_HISTORY_SIZE) {
      newPast.shift();
    }
    
    set({ 
      pipelineHistory: {
        ...state.pipelineHistory,
        [pipelineId]: {
          past: newPast,
          future: [], // Clear future on new action
        }
      }
    });
    logger.debug('Saved to history:', { pipelineId, description, pastLength: newPast.length });
  },

  undo: () => {
    const state = get();
    const pipelineId = state.activePipelineId;
    
    if (!pipelineId) {
      logger.debug('Cannot undo: no active pipeline');
      return;
    }
    
    const history = state.pipelineHistory[pipelineId];
    if (!history || history.past.length === 0) {
      logger.debug('Cannot undo: no history');
      return;
    }
    
    const pipeline = state.pipelines.find(p => p.id === pipelineId);
    if (!pipeline) {
      logger.debug('Cannot undo: pipeline not found');
      return;
    }
    
    // Save current state to future
    const currentState: HistoryEntry = {
      nodes: JSON.parse(JSON.stringify(pipeline.nodes)),
      edges: JSON.parse(JSON.stringify(pipeline.edges)),
      description: 'Current state',
      timestamp: Date.now(),
    };
    
    // Pop from past
    const previousState = history.past[history.past.length - 1];
    const newPast = history.past.slice(0, -1);
    const newFuture = [currentState, ...history.future];
    
    // Update pipeline state
    const newPipelines = state.pipelines.map(p =>
      p.id === pipelineId
        ? {
            ...p,
            nodes: previousState.nodes,
            edges: previousState.edges,
          }
        : p
    );
    
    set({
      pipelineHistory: {
        ...state.pipelineHistory,
        [pipelineId]: {
          past: newPast,
          future: newFuture,
        }
      },
      pipelines: newPipelines,
    });
    
    logger.debug('Undo:', { pipelineId, description: previousState.description });
  },

  redo: () => {
    const state = get();
    const pipelineId = state.activePipelineId;
    
    if (!pipelineId) {
      logger.debug('Cannot redo: no active pipeline');
      return;
    }
    
    const history = state.pipelineHistory[pipelineId];
    if (!history || history.future.length === 0) {
      logger.debug('Cannot redo: no future states');
      return;
    }
    
    const pipeline = state.pipelines.find(p => p.id === pipelineId);
    if (!pipeline) {
      logger.debug('Cannot redo: pipeline not found');
      return;
    }
    
    // Save current state to past
    const currentState: HistoryEntry = {
      nodes: JSON.parse(JSON.stringify(pipeline.nodes)),
      edges: JSON.parse(JSON.stringify(pipeline.edges)),
      description: 'Current state',
      timestamp: Date.now(),
    };
    
    // Pop from future
    const nextState = history.future[0];
    const newFuture = history.future.slice(1);
    const newPast = [...history.past, currentState];
    
    // Update pipeline state
    const newPipelines = state.pipelines.map(p =>
      p.id === pipelineId
        ? {
            ...p,
            nodes: nextState.nodes,
            edges: nextState.edges,
          }
        : p
    );
    
    set({
      pipelineHistory: {
        ...state.pipelineHistory,
        [pipelineId]: {
          past: newPast,
          future: newFuture,
        }
      },
      pipelines: newPipelines,
    });
    
    logger.debug('Redo:', { pipelineId, description: nextState.description });
  },

  canUndo: () => {
    const state = get();
    const pipelineId = state.activePipelineId;
    
    if (!pipelineId) return false;
    
    const history = state.pipelineHistory[pipelineId];
    return history ? history.past.length > 0 : false;
  },

  canRedo: () => {
    const state = get();
    const pipelineId = state.activePipelineId;
    
    if (!pipelineId) return false;
    
    const history = state.pipelineHistory[pipelineId];
    return history ? history.future.length > 0 : false;
  },

  clearHistory: () => {
    const state = get();
    const pipelineId = state.activePipelineId;
    
    if (!pipelineId) {
      logger.debug('Cannot clear history: no active pipeline');
      return;
    }
    
    const { [pipelineId]: _, ...remainingHistory } = state.pipelineHistory;
    
    set({ pipelineHistory: remainingHistory });
    logger.debug('Cleared history for pipeline:', pipelineId);
  },

  clearPipelineHistory: (pipelineId: string) => {
    const state = get();
    
    if (!pipelineId) {
      logger.debug('Cannot clear pipeline history: no pipeline ID provided');
      return;
    }
    
    const { [pipelineId]: _, ...remainingHistory } = state.pipelineHistory;
    
    set({ pipelineHistory: remainingHistory });
    logger.debug('Cleared history for specific pipeline:', pipelineId);
  },
});

